/**
 * Extended deterministic AI Validation checks from
 * AI_Validation_Checks_Dizzaroo_Style workbook (Tone, Safety, Preferred Terms,
 * Document-specific, Numbers/Units, Capitalization, Consistency).
 */

const limitErrors = (errors, max = 12) => {
  const list = Array.isArray(errors) ? errors : [];
  if (list.length <= max) return list;
  return [...list.slice(0, max), `…and ${list.length - max} more issue(s).`];
};

const getCombinedText = (flat) => flat.map((s) => s.text).filter(Boolean).join('\n\n');

const getBodyTextWithoutTables = (flat) =>
  flat
    .map((s) =>
      String(s.html || '')
        .replace(/<table[\s\S]*?<\/table>/gi, ' ')
        .replace(/<[^>]+>/g, ' '),
    )
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();

/** Articles before abbreviations: "an SAE", not "a SAE" */
export const checkAbbreviationArticles = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const vowelSoundStart = /^[AEIOUFHLMNRSX]/i; // phonetic heuristic for A,E,F,H,I,L,M,N,O,R,S,X
  const regex = /\b(a|an)\s+([A-Z]{2,})\b/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    const article = m[1].toLowerCase();
    const abbr = m[2];
    const needsAn = vowelSoundStart.test(abbr[0]);
    if (needsAn && article === 'a') {
      errors.push(`Use "an ${abbr}" (not "a ${abbr}") — article must match phonetic pronunciation.`);
    } else if (!needsAn && article === 'an') {
      errors.push(`Use "a ${abbr}" (not "an ${abbr}") — article must match phonetic pronunciation.`);
    }
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Prefer "participant"; flag "subject(s)" in modern regulatory text */
export const checkSubjectVsParticipant = (flat) => {
  const errors = [];
  // Reference lists quote published titles verbatim ("…in Subjects With Advanced Solid
  // Tumors") — those must not be reworded, so the reference section is excluded.
  const text = getCombinedText(flat.filter((s) => !/references|bibliography|literature/i.test(s.title || '')));
  const regex = /\bsubjects?\b/gi;
  let m;
  while ((m = regex.exec(text)) !== null) {
    // Only "subject" meaning a study participant. Ordinary senses are fine: "documents
    // subject to its review", "the product that is the subject of the complaint".
    const before = text.slice(Math.max(0, m.index - 4), m.index).toLowerCase();
    const after = text.slice(m.index + m[0].length, m.index + m[0].length + 12).toLowerCase();
    if (/^\s+(?:to|of|matter|line)\b/.test(after) || /the\s$/.test(before) && /^\s+of\b/.test(after)) continue;
    errors.push(`Prefer "participant(s)" over "${m[0]}" in modern regulatory documents.`);
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Disease-first language: "cancer patients" → "patients with cancer" */
export const checkDiseaseFirstLanguage = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const patterns = [
    /\bcancer patients?\b/gi,
    /\bdiabetic patients?\b/gi,
    /\basthmatic patients?\b/gi,
    /\bHIV patients?\b/gi,
    /\bobese patients?\b/gi,
  ];
  patterns.forEach((re) => {
    let m;
    while ((m = re.exec(text)) !== null) {
      errors.push(`Use person-first language instead of "${m[0]}" (e.g., "patients with …").`);
    }
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Sex-specific pronouns he/she */
export const checkGenderedPronouns = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const regex = /\bhe\/she\b|\bshe\/he\b|\bhim\/her\b|\bhis\/her\b/gi;
  let m;
  while ((m = regex.exec(text)) !== null) {
    errors.push(`Avoid gendered pronoun pair "${m[0]}" — rewrite to avoid sex-specific pronouns.`);
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Victim/blame language */
export const checkVictimBlameLanguage = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const patterns = [
    /\bsuffering from\b/gi,
    /\bafflicted with\b/gi,
    /\bstricken with\b/gi,
    /\bfailed treatment\b/gi,
    /\bpatient failed\b/gi,
    /\bfailed the (?:drug|therapy|treatment)\b/gi,
  ];
  patterns.forEach((re) => {
    let m;
    while ((m = re.exec(text)) !== null) {
      errors.push(`Avoid victim/blame language "${m[0]}" — rewrite neutrally.`);
    }
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Socioeconomic noun phrases */
export const checkSocioeconomicNounPhrases = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const patterns = [/\bthe poor\b/gi, /\bthe rich\b/gi, /\bthe unemployed\b/gi, /\bthe homeless\b/gi];
  patterns.forEach((re) => {
    let m;
    while ((m = re.exec(text)) !== null) {
      errors.push(`Use person-first socioeconomic language instead of "${m[0]}".`);
    }
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Speculative / promissory language */
export const checkSpeculativeLanguage = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const patterns = [
    /\bguarantee(?:s|d)?\b/gi,
    /\bpromise(?:s|d)?\b/gi,
    /\bmiracle\b/gi,
    /\bbreakthrough\b/gi,
    /\bwill ensure\b/gi,
    /\bwill prove\b/gi,
  ];
  patterns.forEach((re) => {
    let m;
    while ((m = re.exec(text)) !== null) {
      // A negated promise is the cautious form this rule asks for ("these potential
      // benefits are not guaranteed", "no guarantee of benefit").
      if (/\b(?:not|no|cannot|never)\s+(?:be\s+|been\s+)?$/i.test(text.slice(Math.max(0, m.index - 20), m.index))) continue;
      const start = Math.max(0, m.index - 40);
      const context = text.slice(start, m.index + m[0].length + 40).trim();
      errors.push(
        `Avoid speculative/promissory wording "${m[0]}" — may imply unintended commitment: "…${context}…"`
      );
    }
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Placeholder / drafting strings */
export const checkPlaceholderText = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const patterns = [
    /\[TBD\]/gi,
    /\[INSERT[^\]]*\]/gi,
    /\[TK\]/gi,
    /\[TODO\]/gi,
    /\[PLACEHOLDER[^\]]*\]/gi,
    /\blorem ipsum\b/gi,
    /\[Insert[^\]]*\]/g,
  ];
  patterns.forEach((re) => {
    let m;
    while ((m = re.exec(text)) !== null) {
      errors.push(`Unresolved placeholder/drafting text found: "${m[0]}".`);
    }
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Latin dosing abbreviations in body text */
export const checkLatinDosingAbbreviations = (flat) => {
  const errors = [];
  const body = getBodyTextWithoutTables(flat);
  const regex = /\b(bid|tid|qid|qd|prn|qod|q\.d\.|b\.i\.d\.)\b/gi;
  let m;
  while ((m = regex.exec(body)) !== null) {
    errors.push(
      `Spell out Latin dosing abbreviation "${m[0]}" in running text (e.g., twice a day / once daily). Acceptable in tables only.`,
    );
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** ISMP: U / IU for units */
export const checkIsmpUnitsAbbreviations = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const patterns = [
    /\b\d+\s*U\b/g,
    /\b\d+\s*IU\b/g,
    /\b\d+\s*u\b/g,
  ];
  patterns.forEach((re) => {
    let m;
    while ((m = re.exec(text)) !== null) {
      // Skip common false positives like "AU" currency contexts handled loosely
      if (/\bAU\b/.test(m[0])) continue;
      // "20 IU/mL" is a lab/assay concentration (e.g. HBV DNA), not a dose — the
      // ISMP rule is about dose orders, and IU/mL is the standard assay unit.
      if (text[m.index + m[0].length] === '/') continue;
      errors.push(
        `ISMP-FDA: spell out units — found "${m[0]}". Use "units" / "international units" instead of U/IU.`,
      );
    }
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Leading / trailing zeros in doses */
export const checkDoseLeadingTrailingZeros = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const trailing = /\b\d+\.0+\s*(mg|mcg|µg|g|mL|ml|L)\b/gi;
  const leading = /(^|[^\d.])(\.\d+\s*(?:mg|mcg|µg|g|mL|ml|L))\b/gi;
  let m;
  while ((m = trailing.exec(text)) !== null) {
    // "Hemoglobin ≥ 9.0 g/dL" is a lab threshold (concentration), not a dose.
    if (/^\/(?:dL|L|mL|µL|uL)\b/i.test(text.slice(m.index + m[0].length))) continue;
    errors.push(`ISMP: avoid trailing zero in dose "${m[0]}" (e.g., use "1 mg" not "1.0 mg").`);
  }
  while ((m = leading.exec(text)) !== null) {
    errors.push(`ISMP: include leading zero in dose "${m[2]}" (e.g., use "0.5 mg" not ".5 mg").`);
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Prefer mL over cc; mcg over µg in clinical dosing */
export const checkDoseUnitSymbols = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  if (/\b\d+\s*cc\b/i.test(text)) {
    errors.push('Use "mL" instead of "cc" for volume.');
  }
  const ug = text.match(/\b\d+\s*(µg|ug|UG)\b/g) || [];
  ug.forEach((hit) => {
    errors.push(`Prefer "mcg" for micrograms in clinical dosing (found "${hit}").`);
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

// Reference lists quote published titles verbatim ("First-in-Human Phase I Study of …"),
// which must not be reworded — word-choice checks skip them.
const getCombinedTextWithoutReferences = (flat) =>
  getCombinedText(flat.filter((s) => !/references|bibliography|literature/i.test(s.title || '')));

/** Preferred terms from style guide Quick Reference */
export const checkPreferredTerms = (flat) => {
  const errors = [];
  const text = getCombinedTextWithoutReferences(flat);
  const replacements = [
    { re: /\bPhase\s+III\b/g, msg: 'Use "Phase 3" instead of "Phase III".' },
    { re: /\bPhase\s+II\b/g, msg: 'Use "Phase 2" instead of "Phase II".' },
    { re: /\bPhase\s+I\b(?![a-z])/g, msg: 'Use "Phase 1" instead of "Phase I".' },
    { re: /\bprior to\b/gi, msg: 'Prefer "before" instead of "prior to".' },
    { re: /\butilize[ds]?\b/gi, msg: 'Prefer "use" instead of "utilize".' },
    { re: /\bpackage insert\b/gi, msg: 'Prefer "prescribing information" instead of "package insert".' },
    { re: /\bprotocol violations?\b/gi, msg: 'Prefer "protocol deviation(s)" instead of "protocol violation(s)".' },
    { re: /\bInvestigator Brochure\b/g, msg: 'Use "Investigator\'s Brochure".' },
    { re: /\btreatment-naïve\b/gi, msg: 'Use "treatment-naive" without umlaut.' },
    { re: /\bpost-treatment\b/gi, msg: 'Prefer "posttreatment" (no hyphen) per style guide.' },
    { re: /\bcomprised of\b/gi, msg: '"Comprised of" is incorrect — use "comprises" or "composed of".' },
    { re: /\bend result\b/gi, msg: 'Avoid redundant phrase "end result".' },
    { re: /\bcurrently ongoing\b/gi, msg: 'Avoid redundant phrase "currently ongoing".' },
    { re: /\btime period\b/gi, msg: 'Avoid redundant phrase "time period".' },
    { re: /\bwhether or not\b/gi, msg: 'Prefer "whether" instead of "whether or not".' },
    { re: /\beach individual\b/gi, msg: 'Avoid redundant phrase "each individual".' },
  ];
  replacements.forEach(({ re, msg }) => {
    if (re.test(text)) errors.push(msg);
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Prefer symbols >, <, ≥, ≤ over words */
export const checkComparisonWordsVsSymbols = (flat) => {
  const errors = [];
  const text = getCombinedTextWithoutReferences(flat);
  const patterns = [
    { re: /\bgreater than or equal to\b/gi, msg: 'Prefer "≥" instead of "greater than or equal to".' },
    { re: /\bless than or equal to\b/gi, msg: 'Prefer "≤" instead of "less than or equal to".' },
    { re: /\bgreater than\b/gi, msg: 'Prefer ">" instead of "greater than".' },
    { re: /\bless than\b/gi, msg: 'Prefer "<" instead of "less than".' },
    { re: /\bat least\b/gi, msg: 'Where a threshold is intended, prefer "≥" instead of "at least".' },
    { re: /\bno more than\b/gi, msg: 'Prefer "≤" instead of "no more than".' },
  ];
  patterns.forEach(({ re, msg }) => {
    if (re.test(text)) errors.push(msg);
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Spaces around mathematical operators */
export const checkOperatorSpacing = (flat) => {
  const errors = [];
  // Running text only: compact notation in tables ("±1 day" visit windows in the
  // Schedule of Activities, "≥3" grade cells) is standard there. URLs are dropped —
  // query strings ("lookup.cfm?setid=7e67…") aren't mathematical operators.
  const text = getBodyTextWithoutTables(flat)
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\b(?:https?:\/\/|www\.)\S+/gi, ' ');
  const patterns = [
    /[<>≤≥±=]\d/g,
    /\d[<>≤≥±=]/g,
    /\bn=\d/gi,
    /\bN=\d/g,
  ];
  patterns.forEach((re) => {
    let m;
    while ((m = re.exec(text)) !== null) {
      errors.push(`Add spaces around mathematical operator in "${m[0]}" (e.g., "n = 42", "Grade ≥ 3").`);
    }
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Space between number and unit; no space before % */
export const checkNumberUnitSpacing = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const missingSpace = /\b\d+(mg|mcg|µg|kg|g|mL|ml|L|cm|mm)\b/g;
  let m;
  while ((m = missingSpace.exec(text)) !== null) {
    errors.push(`Insert space between numeral and unit: found "${m[0]}" (e.g., "20 mg").`);
  }
  const pctSpace = /\b\d+\s+%/g;
  while ((m = pctSpace.exec(text)) !== null) {
    errors.push(`No space between numeral and "%": found "${m[0]}" (use "85%").`);
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Letter x used as multiply */
export const checkMultiplySymbol = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const regex = /\b\d+\s*[xX]\s*10\s*[⁰¹²³⁴⁵⁶⁷⁸⁹0-9^]/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    errors.push(`Use multiplication symbol × (not letter x) in "${m[0]}".`);
  }
  // also "3 x 10^6"
  const regex2 = /\b\d+\s+[xX]\s+\d/g;
  while ((m = regex2.exec(text)) !== null) {
    errors.push(`Use multiplication symbol × (not letter "${m[0].includes('x') ? 'x' : 'X'}") in "${m[0]}".`);
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Period after unit symbols (mg.) */
export const checkPeriodAfterUnits = (flat) => {
  const errors = [];
  // Table cells (dosing/lab tables) run together without sentence punctuation when
  // flattened to text, which both produces spurious matches and makes every finding
  // below look identical with no way to tell a real one from a table artifact.
  const text = getBodyTextWithoutTables(flat);
  // (?<![A-Za-z.]) keeps the "g." at the end of "e.g." from reading as grams.
  const regex = /(?<![A-Za-z.])\b(mg|mcg|kg|g|mL|ml|L|cm|mm)\.(?!\s*[A-Z0-9])/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    const start = Math.max(0, m.index - 40);
    const context = text.slice(start, m.index + m[0].length + 20).trim();
    errors.push(
      `Do not place a period after unit symbol "${m[1]}." (unless ending a sentence): "…${context}…"`
    );
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Double spaces after punctuation */
export const checkDoubleSpaces = (flat) => {
  const errors = [];
  // Count spaces actually typed in the content. The stripped text joins blocks with
  // added whitespace ("…end.</p><p>Next" -> "end.  Next"), which made every block
  // boundary look like a double space. Line breaks in the HTML source aren't counted.
  const html = flat.map((s) => String(s.html || '')).join('\n');
  const count = (html.match(/[.!?:](?: |\u00a0|&nbsp;){2,}(?=[^\s<])/g) || []).length;
  if (count > 0) {
    errors.push(`Found ${count} instance(s) of multiple spaces after period/colon — use exactly one space.`);
  }
  return { passed: errors.length === 0, errors };
};

/** Lowercase table/figure/section when specific */
export const checkCapitalizedCrossRefDesignators = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const regex = /\b(see|refer to|as shown in|per)\s+(table|figure|section|appendix|listing)\s+\d/gi;
  let m;
  while ((m = regex.exec(text)) !== null) {
    // The trigger phrase ("see"/"refer to"/...) is matched case-insensitively, but the
    // designator itself must actually start lowercase — the `i` flag otherwise also
    // matches already-capitalized "Section 3" and flags it as if it needed fixing.
    const designator = m[2];
    if (designator[0] === designator[0].toUpperCase()) continue;
    errors.push(`Capitalize document designator in "${m[0]}" (e.g., "Table 3", "Section 4.2").`);
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** IND / NCT identifiers presence (soft check) */
export const checkRegulatoryIdentifiers = (flat) => {
  const text = getCombinedText(flat);
  const errors = [];
  const hasNct = /\bNCT\d{8}\b/i.test(text);
  const hasInd = /\bIND\b/i.test(text);
  // One bracketed placeholder ("[IND Number]", "[Insert NCT ID]"). The old greedy
  // \[.*IND.*\] spanned the whole document — from any "[" (e.g. "anti-PD-[L]1") to the
  // last "]" — and matched whenever "IND" appeared anywhere in between.
  const hasPlaceholderInd = /\[[^\]\n]{0,80}\bIND\b[^\]\n]{0,80}\]/i.test(text) || /Insert IND/i.test(text);
  if (hasPlaceholderInd) {
    errors.push('IND identifier placeholder remains unresolved.');
  }
  // Soft: only fail if clearly drafting placeholders for NCT
  if (/\[[^\]\n]{0,80}\bNCT\b[^\]\n]{0,80}\]/i.test(text) || /Insert NCT/i.test(text)) {
    errors.push('ClinicalTrials.gov NCT identifier placeholder remains unresolved.');
  }
  // Informational pass if neither found — still pass (may not be required for all docs)
  if (!hasNct && !hasInd && errors.length === 0) {
    return {
      passed: true,
      errors: [],
      successDetail: 'No IND/NCT placeholders found (identifiers may be optional for this document).',
    };
  }
  return { passed: errors.length === 0, errors };
};

/** Informed consent section present */
export const checkInformedConsentSection = (flat) => {
  const has = flat.some((s) => /informed consent|re-?consent/i.test(s.title || '') || /informed consent/i.test(s.text || ''));
  return {
    passed: has,
    errors: has ? [] : ['No informed consent / re-consenting content found in protocol sections.'],
  };
};

/** Inclusion / exclusion criteria present */
export const checkInclusionExclusionPresent = (flat) => {
  const text = getCombinedText(flat);
  const titles = flat.map((s) => s.title || '').join(' ');
  const hasIncl = /inclusion/i.test(titles) || /inclusion criteria/i.test(text);
  const hasExcl = /exclusion/i.test(titles) || /exclusion criteria/i.test(text);
  const errors = [];
  if (!hasIncl) errors.push('Inclusion criteria section/text not found.');
  if (!hasExcl) errors.push('Exclusion criteria section/text not found.');
  return { passed: errors.length === 0, errors };
};

/** Analysis / safety populations mentioned */
export const checkAnalysisPopulations = (flat) => {
  const text = getCombinedText(flat);
  const errors = [];
  const hasSafety = /\bsafety population\b/i.test(text) || /\bsafety analysis set\b/i.test(text);
  const hasPrimary =
    /\bprimary analysis population\b/i.test(text) ||
    /\bfull analysis set\b/i.test(text) ||
    /\bintention[- ]to[- ]treat\b/i.test(text) ||
    /\bITT\b/.test(text) ||
    /\bFAS\b/.test(text);
  if (!hasPrimary) errors.push('Primary analysis population (ITT/FAS or equivalent) not clearly identified.');
  if (!hasSafety) errors.push('Safety population / safety analysis set not clearly identified.');
  return { passed: errors.length === 0, errors };
};

/** Apostrophe for decade plurals */
export const checkDecadeApostrophe = (flat) => {
  const errors = [];
  const text = getCombinedText(flat);
  const regex = /\b(19|20)\d{2}'s\b/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    errors.push(`No apostrophe for plural decades — use "${m[0].replace("'s", 's')}" not "${m[0]}".`);
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Ranges in running text should use "to" not en/em dash only check for common pattern with units */
export const checkRangesInRunningText = (flat) => {
  const errors = [];
  const body = getBodyTextWithoutTables(flat);
  // Flag en-dash or em-dash numeric ranges in body: 40–50 mL
  const regex = /\b\d+\s*[–—]\s*\d+\s*(mg|mL|ml|kg|g|%|weeks?|days?|hours?)\b/gi;
  let m;
  while ((m = regex.exec(body)) !== null) {
    errors.push(`In running text use "to"/"through" for ranges (found "${m[0]}"). Hyphens OK in tables/parentheses.`);
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Nested parentheses → should use brackets */
export const checkNestedParentheses = (flat) => {
  const errors = [];
  // Parentheses inside a word aren't a nested parenthetical: "anti-PD-(L)1",
  // "(neo)adjuvant" — drop them before looking for real nesting.
  const text = getCombinedText(flat)
    .replace(/(?<=[\w-])\(([A-Za-z0-9]{1,6})\)/g, '$1')
    .replace(/\(([A-Za-z0-9]{1,6})\)(?=[\w-])/g, '$1');
  const regex = /\([^)]*\([^)]*\)[^)]*\)/g;
  let m;
  while ((m = regex.exec(text)) !== null) {
    if (m[0].length < 120) {
      errors.push(`Use square brackets for nested parentheticals: "${m[0].slice(0, 80)}…"`);
    }
  }
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

/** Because/include followed by colon (inline) */
export const checkColonAfterBecauseInclude = (flat) => {
  const errors = [];
  // Runs on HTML, not stripped text: a real <ul>/<ol> list (the normal way protocols
  // format lists) leaves no "•"/"-"/digit marker once tags are stripped, so checking
  // plain text alone always misidentified genuine bulleted lists as violations.
  const html = flat.map((s) => String(s.html || '')).join(' ');
  const patterns = [/\bbecause:\s*/gi, /\binclude:\s*/gi, /\bdue to:\s*/gi];
  patterns.forEach((re) => {
    let m;
    while ((m = re.exec(html)) !== null) {
      // A short bold sub-heading may sit between the colon and its list (the CTFG
      // contraception table: "…DURING THE TRIAL INCLUDE:" / "Highly Effective Methods
      // That Have Low User Dependency" / <ul>…) — skip those before looking for the list.
      const after = html
        .slice(m.index + m[0].length, m.index + m[0].length + 600)
        .replace(
          // A short paragraph opening in bold is a sub-heading, even with a trailing
          // note ("<strong>Highly Effective Methods…</strong> <em>(Failure rate …)</em>").
          /^(?:<p\b[^>]*>\s*<strong>(?:(?!<\/?(?:p|ul|ol|li|table)\b)[\s\S]){1,300}<\/p>|\s|<\/?(?:p|strong|b|em|br)\b[^>]*>)*/i,
          '',
        );
      const introducesRealList = /^(?:\s|<[^>]+>)*<(ul|ol|table)\b/i.test(after);
      const introducesPlainMarker = /^[•\-\d]/.test(after.replace(/<[^>]*>/g, ''));
      if (introducesRealList || introducesPlainMarker) continue;
      errors.push(`Do not use a colon after "${m[0].trim()}" unless introducing a bulleted/numbered list.`);
    }
  });
  return { passed: errors.length === 0, errors: limitErrors(errors) };
};

export const EXTENDED_RULE_CHECKS = [
  {
    id: 'ai-abbrev-articles',
    label: 'Abbreviations: a/an Article Matches Pronunciation',
    category: 'Abbreviation & Terminology',
    run: checkAbbreviationArticles,
  },
  {
    id: 'ai-tone-participant',
    label: 'Tone: Prefer Participant over Subject',
    category: 'Tone & Language',
    run: checkSubjectVsParticipant,
  },
  {
    id: 'ai-tone-person-first',
    label: 'Tone: Person-First Disease Language',
    category: 'Tone & Language',
    run: checkDiseaseFirstLanguage,
  },
  {
    id: 'ai-tone-gendered-pronouns',
    label: 'Tone: Avoid He/She Pronoun Pairs',
    category: 'Tone & Language',
    run: checkGenderedPronouns,
  },
  {
    id: 'ai-tone-victim-blame',
    label: 'Tone: No Victim/Blame Language',
    category: 'Tone & Language',
    run: checkVictimBlameLanguage,
  },
  {
    id: 'ai-tone-socioeconomic',
    label: 'Tone: Person-First Socioeconomic Language',
    category: 'Tone & Language',
    run: checkSocioeconomicNounPhrases,
  },
  {
    id: 'ai-tone-speculative',
    label: 'Tone: No Speculative/Promissory Language',
    category: 'Tone & Language',
    run: checkSpeculativeLanguage,
  },
  {
    id: 'ai-doc-placeholders',
    label: 'Document: No Placeholder/Drafting Text',
    category: 'Document Specific',
    run: checkPlaceholderText,
  },
  {
    id: 'ai-safety-latin-dosing',
    label: 'Safety: Latin Dosing Abbreviations Spelled Out',
    category: 'Safety — Patient & Medication Risk',
    run: checkLatinDosingAbbreviations,
  },
  {
    id: 'ai-safety-ismp-units',
    label: 'Safety: ISMP Units (U/IU Spelled Out)',
    category: 'Safety — Patient & Medication Risk',
    run: checkIsmpUnitsAbbreviations,
  },
  {
    id: 'ai-safety-dose-zeros',
    label: 'Safety: Leading/Trailing Zeros in Doses',
    category: 'Safety — Patient & Medication Risk',
    run: checkDoseLeadingTrailingZeros,
  },
  {
    id: 'ai-units-ml-mcg',
    label: 'Units: mL (not cc) and mcg for Micrograms',
    category: 'Numbers, Units & Symbols',
    run: checkDoseUnitSymbols,
  },
  {
    id: 'ai-consistency-preferred-terms',
    label: 'Consistency: Preferred Terms & Redundancies',
    category: 'Consistency & Terminology',
    run: checkPreferredTerms,
  },
  {
    id: 'ai-symbols-comparison-words',
    label: 'Symbols: Prefer > < ≥ ≤ over Words',
    category: 'Consistency & Terminology',
    run: checkComparisonWordsVsSymbols,
  },
  {
    id: 'ai-numbers-operator-spacing',
    label: 'Numbers: Spaces Around Operators',
    category: 'Numbers, Units & Symbols',
    run: checkOperatorSpacing,
  },
  {
    id: 'ai-numbers-unit-spacing',
    label: 'Numbers: Space Between Numeral and Unit',
    category: 'Numbers, Units & Symbols',
    run: checkNumberUnitSpacing,
  },
  {
    id: 'ai-symbols-multiply',
    label: 'Symbols: Multiplication × (not letter x)',
    category: 'Safety — Patient & Medication Risk',
    run: checkMultiplySymbol,
  },
  {
    id: 'ai-punct-unit-period',
    label: 'Punctuation: No Period After Unit Symbols',
    category: 'Punctuation',
    run: checkPeriodAfterUnits,
  },
  {
    id: 'ai-punct-double-space',
    label: 'Punctuation: Single Space After Period/Colon',
    category: 'Punctuation',
    run: checkDoubleSpaces,
  },
  {
    id: 'ai-xref-capitalize',
    label: 'Cross-References: Capitalize Table/Figure/Section',
    category: 'Cross-References & Navigation',
    run: checkCapitalizedCrossRefDesignators,
  },
  {
    id: 'ai-doc-ind-nct',
    label: 'Document: IND/NCT Identifiers (Placeholders)',
    category: 'Document Specific',
    run: checkRegulatoryIdentifiers,
  },
  {
    id: 'ai-doc-informed-consent',
    label: 'Document: Informed Consent Content Present',
    category: 'Document Specific',
    run: checkInformedConsentSection,
  },
  {
    id: 'ai-doc-incl-excl',
    label: 'Document: Inclusion & Exclusion Criteria Present',
    category: 'Document Specific',
    run: checkInclusionExclusionPresent,
  },
  {
    id: 'ai-doc-analysis-populations',
    label: 'Document: Analysis & Safety Populations Defined',
    category: 'Document Specific',
    run: checkAnalysisPopulations,
  },
  {
    id: 'ai-punct-decade-apostrophe',
    label: 'Punctuation: No Apostrophe in Decade Plurals',
    category: 'Punctuation',
    run: checkDecadeApostrophe,
  },
  {
    id: 'ai-numbers-ranges-text',
    label: 'Numbers: Ranges Use "to" in Running Text',
    category: 'Numbers, Units & Symbols',
    run: checkRangesInRunningText,
  },
  {
    id: 'ai-punct-nested-parens',
    label: 'Punctuation: Brackets for Nested Parentheses',
    category: 'Punctuation',
    run: checkNestedParentheses,
  },
  {
    id: 'ai-punct-colon-because',
    label: 'Punctuation: No Colon After Because/Include',
    category: 'Punctuation',
    run: checkColonAfterBecauseInclude,
  },
];
